import { i18n, t } from 'translate/i18n'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Skeleton from '@material-ui/lab/Skeleton'
import clsx from 'clsx'
import { DropType, OfferType } from 'core/logic/drop/drop.types'
import DropImage from 'components/DropImage'
import ProfileAvatar from 'components/ProfileAvatar'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { FirebaseReducer } from 'react-redux-firebase'
import OfferPrice, { offerPriceParameters } from 'components/offer/OfferPrice'
import useStyles from './DropDetails.style'
import { useTenant } from 'core/logic/tenant/tenant.hook'
import NumberInput from '@onepercentio/one-ui/components/NumberInput'
import { DROP_DETAILS_TEST_IDS } from './DropDetails.e2e'
import { useDropRemainingCount } from 'core/logic/drop/drop.hook'
import Text from 'openspace/components/Text'
import { FigmaTypo } from 'containers/OneUIProvider/OneUIProvider.data'
import AdaptiveContainer from '@onepercentio/one-ui/components/AdaptiveContainer'

interface DropDetailsProps {
  drop?: DropType | null
  price?: number
  title?: boolean
  user?: FirebaseReducer.AuthState
  vertical?: boolean
  onChangeQuantity?(quantity: number): void
}
const DropDetails: React.FC<DropDetailsProps> = ({
  drop,
  title = true,
  user,
  vertical = false,
  price,
  onChangeQuantity,
}) => {
  const classes = useStyles()
  const { tenant } = useTenant()

  const dropData = drop?.[i18n.language] ?? drop
  const remainingUnits = useDropRemainingCount(drop as OfferType)
  const maxUnitsICanBuy = useMemo(
    () =>
      Math.min(
        remainingUnits ?? drop?.maxUnitsPerPurchase!,
        drop?.maxUnitsPerPurchase!
      ),
    [drop, remainingUnits]
  )

  const figureClass = vertical
    ? classes.figure
    : clsx(classes.figure, classes.figureHor)

  const [amount = 1, setAmount] = useState<number | undefined>(1)
  const priceByUnit = offerPriceParameters({
    currency: drop?.currency,
    unitPrice: drop?.unitPrice!,
    prices: Object.fromEntries(
      Object.entries(drop?.prices!).map((a) => [a[0], a[1]])
    ),
  })
  const priceAdjustedForAmount = offerPriceParameters({
    currency: drop?.currency,
    unitPrice: drop?.unitPrice! * amount,
    prices: Object.fromEntries(
      Object.entries(drop?.prices!).map((a) => [a[0], a[1] * amount])
    ),
  })

  useEffect(() => {
    onChangeQuantity?.(amount)
  }, [amount])

  const amountError = useMemo(() => {
    if (amount > maxUnitsICanBuy)
      return t('authenticated.checkout.dropDetails.errors.quantityOver', {
        limit: maxUnitsICanBuy,
      })
  }, [amount, drop, maxUnitsICanBuy])

  return (
    <>
      {title && (
        <Typography variant='subtitle2' paragraph>
          {i18n.t('authenticated.checkout.dropDetails.details')}
        </Typography>
      )}
      <Grid container spacing={0}>
        <Grid item xs={12} md={vertical ? 12 : 6}>
          <Box display='flex' justifyContent='center'>
            <DropImage
              src={drop?.media.icon ?? drop?.image}
              imageClass={classes.image}
              figureClass={figureClass}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={vertical ? 12 : 6}>
          <Typography variant='body2'>
            {i18n.t('authenticated.checkout.dropDetails.recipient')}
          </Typography>
          <Box
            display='flex'
            alignItems='center'
            className={classes.bottomSpacing}>
            <Box mr={1}>
              <ProfileAvatar showUserName={false} />
            </Box>
            <Typography variant='body2' className={classes.userData}>
              {user ? (
                <>
                  <b>{user.displayName}</b>
                  <br />
                  <b>{user.email}</b>
                </>
              ) : (
                <>
                  <Skeleton variant='text' width='100%' height={25} />
                  <Skeleton variant='text' width='100%' height={25} />
                </>
              )}
            </Typography>
          </Box>
          <Typography variant='body2'>
            {i18n.t('authenticated.checkout.dropDetails.itemName')}
          </Typography>
          <Typography variant='body2' className={classes.bottomSpacing}>
            {dropData ? (
              <b>{dropData?.title}</b>
            ) : (
              <Skeleton variant='text' width='80%' height={45} />
            )}
          </Typography>
          {drop?.maxUnitsPerPurchase && (
            <>
              <Typography variant='body2'>
                {i18n.t('authenticated.checkout.dropDetails.quantity')}
              </Typography>
              <NumberInput
                data-testid={DROP_DETAILS_TEST_IDS.QUANTITY}
                onChange={(t) => setAmount(Math.trunc(t!))}
                value={amount}
                error={amountError}
              />
            </>
          )}
          <Typography variant='body2'>
            {i18n.t('authenticated.checkout.dropDetails.value')}
          </Typography>
          <OfferPrice
            {...priceAdjustedForAmount}
            size='sm'
            acceptedCurrencies={tenant?.currencies?.accepted}
            preferredCurrency={tenant?.currencies?.preferredCurrency}
          />
          <AdaptiveContainer direction='v'>
            {amount > 1 ? (
              <Text key={'a'} type={FigmaTypo.P10} color='light'>
                {
                  <OfferPrice
                    {...priceByUnit}
                    acceptedCurrencies={tenant?.currencies?.accepted}
                    preferredCurrency={tenant?.currencies?.preferredCurrency}
                    mode='text'
                  />
                }{' '}
                x {amount} {t('authenticated.checkout.dropDetails.quotas')}
              </Text>
            ) : (
              <Fragment key={'b'} />
            )}
          </AdaptiveContainer>
        </Grid>
      </Grid>
      <Box textAlign='center'></Box>
    </>
  )
}
export default DropDetails
